"use client";

import * as React from "react";

import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { CALCULATOR_CURRENCIES } from "@/lib/pricing-calculator/currencies";

import { useCalculatorEnvironment } from "./context";

interface CurrencySelectProps {
  label: string;
  onChange: (currency: string) => void;
}

/**
 * Currency picker for the calculator, preset to the domain's currency. Names
 * come from `Intl.DisplayNames`, so they follow the page locale without copy.
 * Changing currency relabels the figures; it does not convert them. The
 * closed trigger shows the code alone so it fits beside the heading on
 * mobile; the list carries the full names.
 */
export function CurrencySelect({ label, onChange }: CurrencySelectProps) {
  const { locale, currency } = useCalculatorEnvironment();

  const items = React.useMemo(() => {
    const names = new Intl.DisplayNames([locale], { type: "currency" });
    return CALCULATOR_CURRENCIES.map((code) => ({
      value: code,
      label: `${code} – ${names.of(code) ?? code}`,
    }));
  }, [locale]);
  const selected = items.find((item) => item.value === currency);

  return (
    <Select items={items} value={currency} onValueChange={onChange}>
      <SelectTrigger
        // The trigger shows only the code, so name the full choice for
        // assistive tech.
        aria-label={selected ? `${label}: ${selected.label}` : label}
        className="h-9 w-auto shrink-0 gap-2"
        data-testid="pricing-currency"
      >
        <SelectValue>{(value: string) => value}</SelectValue>
      </SelectTrigger>
      <SelectContent align="end" className="w-auto min-w-(--anchor-width)">
        {items.map((item) => (
          <SelectItem key={item.value} value={item.value}>
            {item.label}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}
